import {
    createDefaultTextureCatalog,
    DEFAULT_TEXTURE_ROTATION_DEG,
    DEFAULT_TEXTURE_SCALE_MM,
    TEST_TEXTURES,
} from "./detailTextures";
import {
    DEFAULT_HANDLE_MATERIAL_ID,
    getHandleMaterialById,
    serializeHandleMaterialCatalog,
    hydrateHandleMaterialCatalog,
} from "./handleMaterials";
import {
    normalizeAxesWithFaceFlip,
    quantizeAxesTo90Degrees,
    eulerForInstancePersist,
    axesToEulerDegrees,
} from "./orientationAxes";
import {
    DEFAULT_LOCAL_ROTATION_ANGLES,
    normalizeLocalRotationAngles,
    resolvePanelPoseFromAxes,
} from "./localRotation";

export const MODULE_3D_STORAGE_VERSION = 8;

/** Исторический размер куба (м) для миграций origin.z в storage. */
export const MODULE_3D_CUBE_SIZE = 1;

const MM_TO_M = 0.001;

/**
 * Миграция local.z при смене origin.z куба.
 * v1/v2/v4/v5/v6/v7: origin.z = −size/2
 * v3:                origin.z = +size/2
 */
export const migratePlacedDetailsOriginZ = (placedDetails, fromVersion) => {
    if (!Array.isArray(placedDetails)) return placedDetails;
    const v = Number(fromVersion);
    if (!(v === 3)) return placedDetails;
    const dz = MODULE_3D_CUBE_SIZE;
    return placedDetails.map((inst) => {
        if (!inst?.localPosition) return inst;
        const z = Number(inst.localPosition.z);
        return {
            ...inst,
            localPosition: {
                ...inst.localPosition,
                z: Number.isFinite(z) ? z + dz : inst.localPosition.z,
            },
        };
    });
};

const isHandleSaved = (inst) =>
    inst?.detailData?.source === "3ds"
    || inst?.detailData?.kind === "handle"
    || inst?.detail?.source === "3ds"
    || inst?.detail?.kind === "handle";

const shiftAlongAxesZ = (inst, sign) => {
    if (!inst?.localPosition || isHandleSaved(inst)) return inst;
    const widthMm = Math.max(Number(inst.detailData?.width) || 0, 0);
    if (!(widthMm > 0)) return inst;
    const w = widthMm * MM_TO_M * sign;
    const az = inst.axes?.z || [0, 0, 1];
    const x = Number(inst.localPosition.x) || 0;
    const y = Number(inst.localPosition.y) || 0;
    const z = Number(inst.localPosition.z) || 0;
    return {
        ...inst,
        localPosition: {
            x: x + w * (Number(az[0]) || 0),
            y: y + w * (Number(az[1]) || 0),
            z: z + w * (Number(az[2]) || 0),
        },
    };
};

/**
 * Ориентация панели по Z:
 * v1–v4, v6: −Z от угла
 * v5, v7:    +Z от угла (как оси куба)
 */
export const migratePlacedDetailsPanelIntoAxes = (placedDetails, fromVersion) => {
    if (!Array.isArray(placedDetails)) return placedDetails;
    const v = Number(fromVersion);
    // v5 уже в +Z (после своего сдвига) — как v7, ничего не делаем.
    // v1–v4 и v6 были в −Z → сдвигаем origin, чтобы тело осталось на месте при +Z.
    if (v === 5 || v >= 7) return placedDetails;
    if (!(v <= 4 || v === 6)) return placedDetails;
    return placedDetails.map((inst) => shiftAlongAxesZ(inst, -1));
};

export const migratePlacedDetails = (placedDetails, fromVersion) =>
    migratePlacedDetailsPanelIntoAxes(
        migratePlacedDetailsOriginZ(placedDetails, fromVersion),
        fromVersion,
    );

export const getModule3DStorageKey = (moduleId) =>
    `up-me:module-3d:${moduleId != null && moduleId !== "" ? moduleId : "unknown"}`;

const TEXTURE_PERSIST_FIELDS = [
    "id",
    "name",
    "url",
    "sourceUrl",
    "defaultScaleMm",
    "defaultRotationDeg",
    "previewZoom",
    "imageZoom",
    "imageRotationDeg",
    "brightness",
    "contrast",
    "tintColor",
    "tintAmount",
    "builtin",
];

/** Сжать каталог для localStorage: у встроенных без правок не храним тяжёлый url. */
export const serializeTextureCatalog = (catalog = []) =>
    (Array.isArray(catalog) ? catalog : [])
        .filter((t) => t?.id)
        .map((t) => {
            const builtinBase = TEST_TEXTURES.find((b) => b.id === t.id);
            const isUneditedBuiltin =
                Boolean(t.builtin)
                && builtinBase
                && t.url === builtinBase.url
                && !String(t.sourceUrl || "").startsWith("data:");

            if (isUneditedBuiltin) {
                return {
                    id: t.id,
                    name: t.name || builtinBase.name,
                    previewZoom: t.previewZoom ?? builtinBase.previewZoom ?? 1,
                    builtin: true,
                };
            }

            const out = {};
            TEXTURE_PERSIST_FIELDS.forEach((key) => {
                if (t[key] !== undefined) out[key] = t[key];
            });
            out.builtin = false;
            if (!out.sourceUrl) out.sourceUrl = out.url;
            return out;
        });

export const hydrateTextureCatalog = (savedCatalog, legacyCustomTextures = []) => {
    if (Array.isArray(savedCatalog)) {
        return savedCatalog
            .map((entry) => {
                if (!entry?.id) return null;
                if (entry.builtin || (!entry.url && TEST_TEXTURES.some((b) => b.id === entry.id))) {
                    const base = TEST_TEXTURES.find((b) => b.id === entry.id);
                    if (!base) return null;
                    return {
                        ...base,
                        name: entry.name || base.name,
                        sourceUrl: base.url,
                        previewZoom: entry.previewZoom ?? base.previewZoom ?? 1,
                        builtin: true,
                    };
                }
                if (!entry.url) return null;
                return {
                    ...entry,
                    sourceUrl: entry.sourceUrl || entry.url,
                    builtin: false,
                };
            })
            .filter(Boolean);
    }

    // Миграция v1: дефолтный каталог + customTextures
    const customs = Array.isArray(legacyCustomTextures)
        ? legacyCustomTextures.filter((t) => t?.id && t?.url)
        : [];
    return [...createDefaultTextureCatalog(), ...customs];
};

const resolveTexture = (textureId, textureUrl, catalog = []) => {
    if (textureId == null && (textureUrl == null || textureUrl === "")) {
        return {textureId: null, textureUrl: null};
    }
    const fromCatalog = catalog.find((t) => t.id === textureId);
    if (fromCatalog?.url) {
        return {
            textureId: fromCatalog.id,
            textureUrl: fromCatalog.url,
            defaultScaleMm: fromCatalog.defaultScaleMm,
            defaultRotationDeg: fromCatalog.defaultRotationDeg,
            previewZoom: fromCatalog.previewZoom,
        };
    }
    if (textureUrl) {
        return {textureId: textureId || null, textureUrl};
    }
    // Текстура удалена из каталога — деталь без материала.
    return {textureId: null, textureUrl: null};
};

const stubDetail = (detail, detailData) => ({
    id: detail?.id ?? null,
    compositionDetailId: detail?.compositionDetailId ?? null,
    name: detail?.name ?? detailData?.name ?? null,
    position: detail?.position ?? detailData?.position ?? null,
});

/** Компактный снимок инстанса. detailData сохраняем — иначе F5 до прихода списка деталей сотрёт сцену. */
export const serializePlacedDetails = (placedDetails = []) =>
    placedDetails.map((inst) => ({
        instanceId: inst.instanceId,
        detailKey: inst.detailKey,
        detailId: inst.detail?.compositionDetailId ?? inst.detail?.id ?? null,
        detail: stubDetail(inst.detail, inst.detailData),
        detailData: inst.detailData ?? null,
        localPosition: inst.localPosition,
        axes: inst.axes,
        faceFlip: Boolean(inst.faceFlip),
        euler: inst.euler,
        textureId: inst.textureId ?? null,
        textureUrl: inst.textureUrl ?? null,
        textureScaleMm: inst.textureScaleMm,
        textureRotationDeg: inst.textureRotationDeg,
        handleMaterialId: inst.handleMaterialId ?? null,
        hidden: Boolean(inst.hidden),
        pacedDetailModuleId: inst.pacedDetailModuleId ?? inst.pacedDetailModule?.id ?? null,
        pacedUrlId: inst.pacedUrlId ?? null,
        pacedDetailModule: inst.pacedDetailModule ?? null,
    }));

export const loadModule3DState = (moduleId) => {
    try {
        const raw = localStorage.getItem(getModule3DStorageKey(moduleId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed) return null;
        const version = Number(parsed.version);
        if (
            version !== 1
            && version !== 2
            && version !== 3
            &&             version !== 4
            && version !== 5
            && version !== 6
            && version !== 7
            && version !== MODULE_3D_STORAGE_VERSION
        ) {
            return null;
        }
        if (!Array.isArray(parsed.placedDetails)) return null;
        return {
            ...parsed,
            version: MODULE_3D_STORAGE_VERSION,
            placedDetails: migratePlacedDetails(parsed.placedDetails, version),
        };
    } catch {
        return null;
    }
};

/** Каталог импортированных DXF-деталей (строки таблицы, не инстансы сцены). */
export const serializeImportedDetails = (importedDetails = []) =>
    (Array.isArray(importedDetails) ? importedDetails : [])
        .filter((row) => row?.detailKey && row?.detail)
        .map((row) => ({
            detailKey: row.detailKey,
            kind: row.kind
                || (row.detail?.source === "3ds" || row.detail?.kind === "handle" ? "handle" : "dxf"),
            detail: row.detail,
        }));

export const hydrateImportedDetails = (savedList = []) => {
    if (!Array.isArray(savedList)) return [];
    return savedList
        .filter((row) => row?.detailKey && row?.detail)
        .map((row) => ({
            detailKey: String(row.detailKey),
            kind: row.kind
                || (row.detail?.source === "3ds" || row.detail?.kind === "handle" ? "handle" : "dxf"),
            detail: row.detail,
        }));
};

export const saveModule3DState = (
    moduleId,
    {
        placedDetails,
        activeTextureId,
        textureCatalog,
        importedDetails = [],
        handleMaterialCatalog,
    },
) => {
    try {
        const payload = {
            version: MODULE_3D_STORAGE_VERSION,
            savedAt: Date.now(),
            activeTextureId: activeTextureId ?? null,
            textureCatalog: serializeTextureCatalog(textureCatalog),
            handleMaterialCatalog: serializeHandleMaterialCatalog(handleMaterialCatalog),
            placedDetails: serializePlacedDetails(placedDetails),
            importedDetails: serializeImportedDetails(importedDetails),
        };
        localStorage.setItem(getModule3DStorageKey(moduleId), JSON.stringify(payload));
        return true;
    } catch (err) {
        console.warn("module-3d: failed to save localStorage", err);
        return false;
    }
};

export const clearModule3DState = (moduleId) => {
    try {
        localStorage.removeItem(getModule3DStorageKey(moduleId));
    } catch {
        // ignore
    }
};

/**
 * Восстановить инстансы из снимка.
 * Предпочитаем сохранённый detailData; при наличии списка модуля — подставляем актуальный detail.
 */
export const hydratePlacedDetails = async (
    savedList,
    details,
    resolveDetailData,
    getDetailKey,
    textureCatalog = [],
    handleMaterialCatalog = null,
) => {
    if (!Array.isArray(savedList) || !savedList.length) return [];

    const restored = [];
    for (const saved of savedList) {
        if (!saved?.instanceId || !saved?.localPosition || !saved?.axes) continue;

        let detail = null;
        if (saved.detailId != null && Array.isArray(details)) {
            detail = details.find(
                (d) => {
                    const tableId = d?.compositionDetailId ?? d?.id;
                    return tableId === saved.detailId || String(tableId) === String(saved.detailId);
                },
            );
        }
        if (!detail && saved.detailKey != null && Array.isArray(details)) {
            detail = details.find((d, idx) => getDetailKey(d, idx) === saved.detailKey);
        }
        if (!detail) {
            detail = saved.detail ?? (saved.detailId != null ? {id: saved.detailId} : null);
        }
        if (!detail && !saved.detailData) continue;

        const detailIndex = Array.isArray(details) ? details.indexOf(detail) : -1;
        const detailKey =
            saved.detailKey
            ?? (detail ? getDetailKey(detail, detailIndex >= 0 ? detailIndex : 0) : String(saved.instanceId));

        let detailData = saved.detailData;
        if (!detailData && detail) {
            detailData = await resolveDetailData(detail);
        }
        if (!detailData) continue;

        if (detail && Array.isArray(details) && details.includes(detail)) {
            try {
                const resolved = await resolveDetailData(detail);
                if (resolved) detailData = resolved;
            } catch {
                // keep snapshot
            }
        }

        const {textureId, textureUrl} = resolveTexture(
            saved.textureId,
            saved.textureUrl,
            textureCatalog,
        );

        const isHandle = saved.detailData?.source === "3ds"
            || saved.detailData?.kind === "handle"
            || saved.detail?.source === "3ds"
            || saved.detail?.kind === "handle";
        const handleMaterialId = isHandle
            ? getHandleMaterialById(saved.handleMaterialId, handleMaterialCatalog).id
            : (saved.handleMaterialId ?? null);

        let axes;
        let faceFlip;
        let referenceAxes;
        let localRotationAngles;
        if (isHandle) {
            const quant = quantizeAxesTo90Degrees(
                normalizeAxesWithFaceFlip(saved.axes, saved.faceFlip ?? false).axes,
                saved.faceFlip ?? false,
            );
            axes = quant.axes;
            faceFlip = quant.faceFlip;
            referenceAxes = quant.referenceAxes;
            localRotationAngles = quant.localRotationAngles;
        } else {
            const pose = resolvePanelPoseFromAxes({
                axes: saved.axes,
                faceFlip: saved.faceFlip ?? false,
                referenceAxes: saved.referenceAxes,
                localRotationAngles: saved.localRotationAngles ?? DEFAULT_LOCAL_ROTATION_ANGLES,
            });
            axes = pose.axes;
            faceFlip = pose.faceFlip;
            referenceAxes = pose.referenceAxes;
            localRotationAngles = pose.localRotationAngles;
        }

        restored.push({
            instanceId: saved.instanceId,
            detail: detail ?? stubDetail(null, detailData),
            detailData,
            detailKey,
            localPosition: saved.localPosition,
            axes,
            referenceAxes,
            localRotationAngles,
            faceFlip,
            euler: eulerForInstancePersist(axes, saved.euler ?? axesToEulerDegrees(axes)),
            textureId,
            textureUrl,
            textureScaleMm: saved.textureScaleMm ?? DEFAULT_TEXTURE_SCALE_MM,
            textureRotationDeg: saved.textureRotationDeg ?? DEFAULT_TEXTURE_ROTATION_DEG,
            handleMaterialId: isHandle
                ? (handleMaterialId || DEFAULT_HANDLE_MATERIAL_ID)
                : null,
            pacedDetailModuleId: saved.pacedDetailModuleId
                ?? saved.pacedDetailModule?.id
                ?? null,
            pacedUrlId: saved.pacedUrlId ?? null,
            pacedDetailModule: saved.pacedDetailModule ?? null,
            hidden: Boolean(saved.hidden),
        });
    }
    return restored;
};
